/**
 * Storage Manager for Cloudflare R2
 *
 * Uses Cloudflare Worker API to dynamically list files from R2 bucket.
 * Falls back to hardcoded list if Worker is unavailable.
 */

import { FeaturedWorkKey, ImageType } from "module/types";

const R2_PUBLIC_URL = process.env.REACT_APP_R2_PUBLIC_URL || "";
const R2_WORKER_URL = process.env.REACT_APP_R2_WORKER_URL || "";

enum StorageRootDirectory {
  FeaturedWorks = "featured_works",
}

interface R2FileInfo {
  name: string;
  size: number;
  uploaded: string;
}

interface R2ListResponse {
  files: R2FileInfo[];
  prefix: string;
}

// 캐시 저장소
const fileListCache: Map<string, string[]> = new Map();

/**
 * Worker API를 통해 R2 폴더의 파일 목록 조회
 */
async function fetchFileList(prefix: string): Promise<string[]> {
  // 캐시 확인
  if (fileListCache.has(prefix)) {
    return fileListCache.get(prefix)!;
  }

  if (!R2_WORKER_URL) {
    console.warn("REACT_APP_R2_WORKER_URL is not defined. Using fallback.");
    return [];
  }

  try {
    const response = await fetch(
      `${R2_WORKER_URL}/list?prefix=${encodeURIComponent(prefix)}`
    );

    if (!response.ok) {
      throw new Error(`Worker API error: ${response.status}`);
    }

    const data: R2ListResponse = await response.json();
    const fileNames = data.files.map((f) => f.name);

    // 캐시에 저장
    fileListCache.set(prefix, fileNames);

    return fileNames;
  } catch (error) {
    console.error("Failed to fetch file list from Worker:", error);
    return [];
  }
}

/**
 * 이미지 파일명에서 orientation 추출
 */
const getImageOrientation = (filename: string): "horizontal" | "vertical" => {
  return filename.includes("vertical") ? "vertical" : "horizontal";
};

/**
 * R2 URL 생성 헬퍼
 */
const buildR2Url = (path: string): string => {
  if (!R2_PUBLIC_URL) {
    console.error(
      "REACT_APP_R2_PUBLIC_URL is not defined. Please set it in your environment variables."
    );
    return "";
  }
  const baseUrl = R2_PUBLIC_URL.endsWith("/")
    ? R2_PUBLIC_URL.slice(0, -1)
    : R2_PUBLIC_URL;
  return `${baseUrl}/${path}`;
};

/**
 * Featured Works 썸네일 이미지 맵 가져오기
 * Worker API를 통해 동적으로 파일 목록 조회
 */
export async function getFeaturedWorksThumbnails(): Promise<
  Record<FeaturedWorkKey, ImageType>
> {
  const thumbnails: Record<string, ImageType> = {};
  const prefix = `${StorageRootDirectory.FeaturedWorks}/thumbnails/`;

  // Worker API로 파일 목록 조회
  const files = await fetchFileList(prefix);

  if (files.length > 0) {
    // 동적 조회 성공
    files.forEach((filename) => {
      // 파일명에서 key 추출 (예: werewolf_online.png -> werewolf_online)
      const keyName = filename.split(".")[0];
      const key = Object.values(FeaturedWorkKey).find((k) => k === keyName);

      if (key) {
        const url = buildR2Url(`${prefix}${filename}`);
        thumbnails[key] = {
          url,
          orientation: "horizontal",
        };
      }
    });
  } else {
    // Fallback: 모든 FeaturedWorkKey에 대해 기본 확장자로 URL 생성
    Object.values(FeaturedWorkKey).forEach((key) => {
      const thumbnailPath = `${prefix}${key}.png`;
      const url = buildR2Url(thumbnailPath);

      thumbnails[key] = {
        url,
        orientation: "horizontal",
      };
    });
  }

  return thumbnails as Record<FeaturedWorkKey, ImageType>;
}

/**
 * 특정 Featured Work의 상세 이미지 목록 가져오기
 * Worker API를 통해 동적으로 파일 목록 조회
 */
export async function getFeaturedWorksImages(
  featuredWork: FeaturedWorkKey
): Promise<ImageType[]> {
  const prefix = `${StorageRootDirectory.FeaturedWorks}/${featuredWork}/`;

  // Worker API로 파일 목록 조회
  const files = await fetchFileList(prefix);

  if (files.length > 0) {
    // 동적 조회 성공 - 폴더 경로 제외
    return files
      .filter((filename) => filename && !filename.endsWith("/"))
      .map((filename) => {
        const url = buildR2Url(`${prefix}${filename}`);
        const orientation = getImageOrientation(filename);
        return { url, orientation };
      });
  }

  // Fallback: 빈 배열 반환 (Worker 없으면 상세 이미지 표시 불가)
  console.warn(`No images found for ${featuredWork}`);
  return [];
}

/**
 * R2 Public URL 확인 (디버깅용)
 */
export const getR2PublicUrl = (): string => {
  return R2_PUBLIC_URL;
};

/**
 * R2 Worker URL 확인 (디버깅용)
 */
export const getR2WorkerUrl = (): string => {
  return R2_WORKER_URL;
};

/**
 * 파일 목록 캐시 초기화 (디버깅용)
 */
export const clearFileListCache = (): void => {
  fileListCache.clear();
};
